// src/components/ServiceBox.js
import React, { useContext } from "react";
import {
  Box,
  Heading,
  Grid,
  GridItem,
  Center,
  useTheme,
  Card,
  CardBody,
  CardFooter,
  Stack,
  VStack,
  Image,
  Button,
  Divider,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext"; // นำเข้า AuthContext
// import { VaxReserveIcon, VaxInfoIcon, DeseaseInfoIcon } from "./CustomIcon"; // These icons are imported but not used in this component. Consider removing if not needed.

const ServiceBox = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { loggedInUser } = useContext(AuthContext); // ดึงสถานะ login จาก context

  // Define responsive sizes using clamp
  const circleImageSize = "clamp(140px, 14vw, 180px)"; // Adjusted from "200px", "240px", "280px"
                                                       // Using vw as it's inside a grid, relates to screen width
                                                       // Alternative: "clamp(140px, 22vmin, 240px)" if viewport's smaller dimension is preferred
  const circleBorderWidth = "clamp(3px, 0.5vw, 5px)";   // Responsive border width
  const cardTitleFontSize = "clamp(0.9rem, 1.8vw, 1.2rem)"; 
  const cardSubtitleFontSize = "clamp(0.6rem, 1.8vw, 0.8rem)";
  const buttonTextFontSize = "clamp(0.5rem, 1.8vw, 0.8rem)"; 

  const handleVaxReserveClick = () => {
      navigate("/vaccinereservationlist");
  };

  const handleVaxInfoClick = () => {
    navigate("/vaccineinfo");
  };

  const handleDiseaseInfoClick = () => {
    navigate("/diceaseinfo"); // Corrected typo from diceaseinfo to diseaseinfo, assuming that's the intended path. If /diceaseinfo is correct, revert this.
                                // Original was "/diceaseinfo"
  };

  return (
    <Grid
      templateColumns={{
        base: "1fr", // 1 column on small screens
        lg: "repeat(3, 1fr)", // 2 columns on large screens
        xl: "repeat(3, 1fr)", // 3 columns on extra-large screens
      }}
      gap={5}
      alignItems="start"
      p={{ base: "0.8rem", md: "1rem" }} // Add some padding to the grid itself
    >
      {/* Card 1: สั่งจองวัคซีน */}
      <GridItem w="100%">
        <Center h="100%">
          <Card
            maxW="md" // Keeps cards from getting too wide
            boxShadow="none"
            w="100%" // Ensure card takes full width of GridItem
            display="flex"
            flexDirection="column"
            height="100%" // Make cards in a row equal height
          >
            <CardBody flex="1"> {/* Allow CardBody to grow */}
              <VStack
                w={circleImageSize} // Apply responsive size
                h={circleImageSize} // Apply responsive size
                bg="white"
                borderColor={theme.colors.custGreen}
                borderRadius="full"
                borderWidth={circleBorderWidth} // Apply responsive border width
                boxShadow="2xl"
                justifyContent="center"
                alignItems="center"
                overflow="hidden"
                p={0}
                mx="auto" // Center the VStack horizontally
                transition="transform 0.3s ease"
                _hover={{ transform: "scale(1.05)" }}
              >
                <Image
                  src="/landingimages/vaxreservation.png"
                  alt="สั่งจองวัคซีน"
                  objectFit="cover"
                  w="100%"
                  h="100%"
                />
              </VStack>
              <Box
                bg={theme.colors.custGreen}
                bgPosition="center"
                bgRepeat="no-repeat"
                bgSize="100% 100%"
                w="full"
                mt={5}
                h={1}
              />
              <Stack mt="6" spacing="3">
                <Heading fontSize={cardTitleFontSize} color={theme.colors.custGreen}>
                  สั่งจองวัคซีน
                </Heading>
                <Heading fontSize={cardSubtitleFontSize} color={theme.colors.custGreen}>
                  จองวัคซีนออนไลน์ จองง่าย ได้วัคซีนตามต้องการ
                </Heading>
              </Stack>
            </CardBody>
            <Divider color={theme.colors.custGreen} />
            <CardFooter style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="outline"
                color={theme.colors.custGreen}
                borderRadius="lg"
                onClick={handleVaxReserveClick}
              >
                <Heading
                  fontSize={buttonTextFontSize}
                  color={theme.colors.custGreen}
                  fontWeight="normal"
                >
                  สั่งจองวัคซีน
                </Heading>
              </Button>
            </CardFooter>
          </Card>
        </Center>
      </GridItem>

      {/* Card 2: ข้อมูลวัคซีน */}
      <GridItem w="100%">
        <Center h="100%">
          <Card
            maxW="md"
            boxShadow="none"
            w="100%"
            display="flex"
            flexDirection="column"
            height="100%"
          >
            <CardBody flex="1">
              <VStack
                w={circleImageSize} // Apply responsive size
                h={circleImageSize} // Apply responsive size
                bg="white"
                borderColor={theme.colors.custBlue}
                borderRadius="full"
                borderWidth={circleBorderWidth} // Apply responsive border width
                boxShadow="2xl"
                justifyContent="center"
                alignItems="center"
                overflow="hidden"
                p={0}
                mx="auto"
                transition="transform 0.3s ease"
                _hover={{ transform: "scale(1.05)" }}
              >
                <Image
                  src="/landingimages/vaxinfo.png"
                  alt="ข้อมูลวัคซีน"
                  objectFit="cover"
                  w="100%"
                  h="100%"
                />
              </VStack>
              <Box
                bg={theme.colors.custBlue}
                bgPosition="center"
                bgRepeat="no-repeat"
                bgSize="100% 100%"
                w="full"
                mt={5}
                h={1}
              />
              <Stack mt="6" spacing="3">
                <Heading fontSize={cardTitleFontSize} color={theme.colors.custBlue}>
                  ข้อมูลวัคซีน
                </Heading>
                <Heading fontSize={cardSubtitleFontSize} color={theme.colors.custBlue}>
                  ศูนย์รวมข้อมูลวัคซีนปศุสัตว์ รู้จักวัคซีนชนิดต่างๆ และวิธีการใช้งาน
                </Heading>
              </Stack>
            </CardBody>
            <Divider color={theme.colors.custBlue} />
            <CardFooter style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="outline"
                color={theme.colors.custBlue}
                borderRadius="lg"
                onClick={handleVaxInfoClick}
              >
                <Heading
                  fontSize={buttonTextFontSize}
                  color={theme.colors.custBlue}
                  fontWeight="normal"
                >
                  ข้อมูลวัคซีน
                </Heading>
              </Button>
            </CardFooter>
          </Card>
        </Center>
      </GridItem>

      {/* Card 3: ข้อมูลโรคในสัตว์ */}
      <GridItem w="100%">
        <Center h="100%">
          <Card
            maxW="md"
            boxShadow="none"
            w="100%"
            display="flex"
            flexDirection="column"
            height="100%"
          >
            <CardBody flex="1">
              <VStack
                w={circleImageSize} // Apply responsive size
                h={circleImageSize} // Apply responsive size
                bg="white"
                borderColor={theme.colors.custPurple}
                borderRadius="full"
                borderWidth={circleBorderWidth} // Apply responsive border width
                boxShadow="2xl"
                justifyContent="center"
                alignItems="center"
                overflow="hidden"
                p={0}
                mx="auto"
                transition="transform 0.3s ease"
                _hover={{ transform: "scale(1.05)" }}
              >
                <Image
                  src="/landingimages/diseaseinfo.png"
                  alt="ข้อมูลโรคในสัตว์"
                  objectFit="cover"
                  w="100%"
                  h="100%"
                />
              </VStack>
              <Box
                bg={theme.colors.custPurple}
                bgPosition="center"
                bgRepeat="no-repeat"
                bgSize="100% 100%"
                w="full"
                mt={5}
                h={1}
              />
              <Stack mt="6" spacing="3">
                <Heading fontSize={cardTitleFontSize} color={theme.colors.custPurple}>
                  ข้อมูลโรคในสัตว์
                </Heading>
                <Heading fontSize={cardSubtitleFontSize} color={theme.colors.custPurple}>
                รู้ทันโรค การป้องกัน และแนวทางการรักษาอย่างถูกวิธี
                </Heading>
              </Stack>
            </CardBody>
            <Divider color={theme.colors.custPurple} />
            <CardFooter style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="outline"
                color={theme.colors.custPurple}
                borderRadius="lg"
                onClick={handleDiseaseInfoClick}
              >
                <Heading
                  fontSize={buttonTextFontSize}
                  color={theme.colors.custPurple}
                  fontWeight="normal"
                >
                  ข้อมูลโรคในสัตว์
                </Heading>
              </Button>
            </CardFooter>
          </Card>
        </Center>
      </GridItem>
    </Grid>
  );
};

export default ServiceBox;